<template>
  <div class="routine-plan-page" v-loading="loading">
    <!-- 顶栏：项目信息 + 操作 -->
    <div class="plan-hero">
      <div class="hero-main">
        <div class="hero-title-row">
          <h1 class="hero-title">周期任务安排</h1>
          <el-tag v-if="project" size="small" :type="phaseTagType">{{ phaseLabel }}</el-tag>
        </div>
        <div class="hero-meta">
          <span class="meta-item"><label>项目编号</label>{{ xmbh }}</span>
          <span v-if="project" class="meta-item"><label>任务名称</label>{{ project.xmmc || '—' }}</span>
          <span v-if="project" class="meta-item"><label>委托企业</label>{{ project.wtdw_mc || '—' }}</span>
          <span v-if="project && project.assignee_xm" class="meta-item"><label>接收人</label>{{ project.assignee_xm }}</span>
        </div>
        <div v-if="itemSummary.length" class="hero-items">
          <div v-for="(it, i) in itemSummary" :key="i" class="hero-item-chip">
            <span class="chip-place">{{ it.place }}</span>
            <span class="chip-sep">·</span>
            <span>{{ it.items }}</span>
            <span class="chip-sep">·</span>
            <span>{{ it.freq }}</span>
            <span class="chip-sep">·</span>
            <span>排期 {{ it.schedule }}</span>
          </div>
        </div>
      </div>
      <div class="hero-actions">
        <div class="stat-pills">
          <div class="stat-pill">
            <span class="stat-num">{{ picks.length }}</span>
            <span class="stat-label">期次</span>
          </div>
          <div class="stat-pill">
            <span class="stat-num">{{ dispatches.length }}</span>
            <span class="stat-label">派遣单</span>
          </div>
        </div>
        <template v-if="planEditable">
          <div class="action-row">
            <el-select
              v-model="bulkSamplerId"
              filterable
              clearable
              size="small"
              placeholder="统一采样人"
              class="bulk-select"
            >
              <el-option v-for="u in samplers" :key="u.id" :label="samplerLabel(u)" :value="samplerId(u)" />
            </el-select>
            <el-button size="small" :disabled="!bulkSamplerId" @click="applyBulkSampler">应用到可改期次</el-button>
          </div>
          <div class="action-hint">统一指定后仍可对单行重选；已采样期次不可改</div>
          <div class="action-row">
            <el-button size="small" @click="savePlan">保存安排</el-button>
            <el-button type="primary" :loading="confirming" @click="confirmPlan">确认并生成派遣</el-button>
          </div>
        </template>
        <el-button
          v-if="showUpdateDispatch"
          type="primary"
          plain
          size="small"
          :loading="updatingDispatch"
          @click="updateDispatchDefaults"
        >更新派遣信息</el-button>
      </div>
    </div>

    <el-alert
      v-if="showUpdateDispatch"
      type="info"
      show-icon
      :closable="false"
      title="已派遣：可修改采样人、计划日与各行派遣补充，改完后点「更新派遣信息」保存"
      class="plan-alert"
    />

    <el-alert
      v-else-if="!canEdit && project"
      :title="readonlyHint"
      type="warning"
      show-icon
      :closable="false"
      class="plan-alert"
    />

    <div class="plan-body" :class="{ 'plan-body-full': !dispatches.length && planEditable }">
      <!-- 左：月度安排 -->
      <el-card shadow="never" class="plan-card plan-card-main">
        <div slot="header" class="card-head">
          <span class="card-head-title">月度采样安排</span>
          <span class="card-head-hint">点击行首展开填写本期派遣补充</span>
        </div>
        <el-table
          ref="pickTable"
          :data="picks"
          border
          size="small"
          class="pick-table"
          row-key="id"
          :default-expand-all="planEditable || canEditDispatch"
        >
          <el-table-column type="expand">
            <template slot-scope="{ row }">
              <div class="pick-extra-panel">
                <div class="pick-extra-card">
                  <div class="pick-extra-label">派遣补充（本期）</div>
                  <el-form label-width="96px" size="small" class="pick-extra-form">
                    <DispatchFormExtra
                      :value="row.dispatch_extra || {}"
                      :users="userOptions"
                      :readonly="!pickFieldsEditable"
                      :show-cycsts="true"
                      :show-handover-time="false"
                      :show-signatures="false"
                      compact
                      @input="val => onPickExtraChange(row, val)"
                    />
                  </el-form>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="期次" width="72" align="center" fixed>
            <template slot-scope="{ row }">
              <span class="period-badge">{{ row.plan_month }}月·{{ row.plan_period }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="monitor_place" label="监测点位" min-width="110" show-overflow-tooltip />
          <el-table-column label="采样人员" width="150">
            <template slot-scope="{ row }">
              <el-select
                v-if="pickFieldsEditable && !isPickSamplerLocked(row)"
                v-model="row.sampler_id"
                filterable
                clearable
                size="mini"
                placeholder="选择 / 重选"
                @change="onPickSamplerChange(row)"
              >
                <el-option v-for="u in samplers" :key="u.id" :label="u.xm" :value="samplerId(u)" />
              </el-select>
              <div v-else class="sampler-locked-cell">
                <span class="cell-text">{{ row.sampler_xm || '—' }}</span>
                <el-tag v-if="isPickSamplerLocked(row)" size="mini" type="info" class="lock-tag">已采样</el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="计划采样日" width="148">
            <template slot-scope="{ row }">
              <el-date-picker
                v-model="row.pick_date"
                type="date"
                value-format="yyyy-MM-dd"
                size="mini"
                placeholder="选择日期"
                style="width:128px"
                :disabled="!pickFieldsEditable"
              />
            </template>
          </el-table-column>
          <el-table-column label="状态" width="88" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="pickStatusType(row)">{{ pickStatusLabel(row) }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- 右：已生成派遣单摘要（只读） -->
      <div v-if="dispatches.length || !planEditable" class="plan-side">
        <el-card v-if="dispatches.length" shadow="never" class="plan-card dispatch-card">
          <div slot="header" class="card-head">
            <span class="card-head-title">已生成派遣单</span>
            <span class="card-head-hint">{{ dispatches.length }} 张 · 点击定位到对应期次</span>
          </div>
          <div class="dispatch-list">
            <div
              v-for="row in dispatches"
              :key="row.id"
              class="dispatch-item"
              :class="{ 'is-active': activeDispatchId === row.id }"
              @click="focusPickForDispatch(row)"
            >
              <div class="dispatch-item-top">
                <span class="dispatch-rwbh">{{ dispatchSj(row).rwbh || row.rwbh || '—' }}</span>
                <el-tag size="mini" type="info">{{ row.bdzt || 'draft' }}</el-tag>
              </div>
              <div class="dispatch-item-meta">
                <span>{{ dispatchSj(row).cyry || '—' }}</span>
                <span class="meta-dot">·</span>
                <span>{{ dispatchPeriodLabel(row) }}</span>
              </div>
              <div v-if="dispatchSj(row).pqdd" class="dispatch-item-place">{{ dispatchSj(row).pqdd }}</div>
            </div>
          </div>
        </el-card>

        <el-card v-else-if="!planEditable" shadow="never" class="plan-card plan-card-empty">
          <el-empty description="暂无派遣单" :image-size="64" />
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'
import { canEditRoutinePlan } from '@/utils/user'
import DispatchFormExtra from '@/components/DispatchFormExtra.vue'
import { displayItemNames, displayMonitorPlace } from '@/utils/siteMonitorMap'

const XMZT_LABELS = {
  pending_audit: '待审核',
  audit_pass: '待安排',
  plan_ready: '安排中',
  dispatched: '已派遣',
  sampling: '采样中',
  handover: '样品交接',
  lab_ready: '待理化',
  labing: '理化中',
  lab_done: '理化完成',
  reporting: '编报告中',
  done: '已完成'
}

export default {
  components: { DispatchFormExtra },
  data() {
    return {
      loading: false,
      confirming: false,
      updatingDispatch: false,
      xmbh: '',
      picks: [],
      items: [],
      samplers: [],
      users: [],
      project: null,
      canEdit: false,
      canEditDispatch: false,
      dispatches: [],
      bulkSamplerId: null,
      activeDispatchId: null
    }
  },
  watch: {
    '$route.params.xmbh': { immediate: true, handler(v) { if (v) { this.xmbh = v; this.load() } } }
  },
  computed: {
    readonlyHint() {
      const who = (this.project && this.project.assignee_xm) || '接收人'
      return `周期任务安排由 ${who} 负责，您当前仅可查看`
    },
    planEditable() {
      if (!this.canEdit || !this.project) return false
      return ['audit_pass', 'plan_ready'].includes(this.project.xmzt)
    },
    pickFieldsEditable() {
      return this.planEditable || this.canEditDispatch
    },
    showUpdateDispatch() {
      return this.canEditDispatch && this.dispatches.length > 0 && !this.planEditable
    },
    userOptions() {
      return this.users.length ? this.users : this.samplers
    },
    itemSummary() {
      return (this.items || []).map(it => ({
        place: displayMonitorPlace(it),
        items: displayItemNames(it) || '—',
        freq: this.freqText(it),
        schedule: this.scheduleSummary(it)
      }))
    },
    phaseLabel() {
      const zt = this.project && this.project.xmzt
      return XMZT_LABELS[zt] || zt || '—'
    },
    phaseTagType() {
      const zt = this.project && this.project.xmzt
      const m = {
        audit_pass: 'warning', plan_ready: 'info', dispatched: 'success',
        sampling: 'warning', handover: 'warning', done: 'success'
      }
      return m[zt] || 'info'
    }
  },
  mounted() {
    this.loadUsers()
  },
  methods: {
    samplerId(u) { return parseInt(u.id, 10) },
    samplerLabel(u) {
      const role = (u.jsmc_ls || '').trim()
      return role ? `${u.xm}（${role}）` : u.xm
    },
    freqText(row) {
      const fc = parseInt(row.freq_count || row.freq_per_day || 1, 10) || 1
      const pc = parseInt(row.periods_count || 1, 10) || 1
      const fl = row.freq_label ? `(${row.freq_label})` : ''
      if (row.freq_unit === 'day') return `${fc}次/天${fl}`
      return `${fc}次/期, 共${pc}期${fl}`
    },
    scheduleSummary(row) {
      const sch = row.schedule_json
      if (!sch) return '—'
      let obj = sch
      if (typeof sch === 'string') {
        try { obj = JSON.parse(sch) } catch (e) { return '—' }
      }
      const months = []
      for (let m = 1; m <= 12; m++) {
        if (obj[String(m)] || obj[m]) months.push(m)
      }
      return months.length ? months.join(',') + '月' : '—'
    },
    isPickSamplerLocked(row) {
      if (!row) return false
      return !!(row.sampler_locked === 1 || row.sampler_locked === true)
    },
    pickStatusLabel(row) {
      if (this.isPickSamplerLocked(row)) return '已采样'
      const s = row && row.status
      return { pending: '待安排', confirmed: '已派遣' }[s] || s || '—'
    },
    pickStatusType(row) {
      if (this.isPickSamplerLocked(row)) return 'danger'
      const s = row && row.status
      return s === 'confirmed' ? 'success' : 'info'
    },
    dispatchSj(row) {
      try { return JSON.parse(row.sj_json || '{}') } catch (e) { return {} }
    },
    dispatchPeriodLabel(row) {
      const s = this.dispatchSj(row)
      if (s.yqwcsj) return s.yqwcsj
      if (s.bz) return s.bz
      const parts = []
      if (s.plan_period) parts.push('第' + s.plan_period + '期')
      if (s.plan_month) parts.push(s.plan_month + '月')
      return parts.length ? parts.join(' · ') : '—'
    },
    onPickSamplerChange(pick) {
      if (this.isPickSamplerLocked(pick)) {
        this.$message.warning(pick.sampler_lock_reason || '该期次已采样，不可更换采样人')
        return
      }
      const u = this.samplers.find(x => this.samplerId(x) === parseInt(pick.sampler_id, 10))
      pick.sampler_xm = u ? u.xm : ''
      if (!pick.dispatch_extra) this.$set(pick, 'dispatch_extra', {})
      if (pick.sampler_xm) {
        this.$set(pick.dispatch_extra, 'ypjjry', pick.sampler_xm)
      }
    },
    onPickExtraChange(row, val) {
      this.$set(row, 'dispatch_extra', val || {})
    },
    applyBulkSampler() {
      if (!this.bulkSamplerId) return
      const u = this.samplers.find(x => this.samplerId(x) === parseInt(this.bulkSamplerId, 10))
      const xm = u ? u.xm : ''
      let applied = 0
      let skipped = 0
      this.picks.forEach(p => {
        if (this.isPickSamplerLocked(p)) {
          skipped += 1
          return
        }
        p.sampler_id = this.bulkSamplerId
        p.sampler_xm = xm
        if (!p.dispatch_extra) this.$set(p, 'dispatch_extra', {})
        this.$set(p.dispatch_extra, 'ypjjry', xm)
        applied += 1
      })
      if (!applied && skipped) {
        this.$message.warning(`全部 ${skipped} 个期次已采样，未改动`)
        return
      }
      const tip = skipped
        ? `已应用到 ${applied} 个期次，跳过已采样 ${skipped} 个`
        : `已统一指定采样人（${applied} 个期次），可对单行重选`
      this.$message.success(tip)
    },
    seedDispatchExtraForPick(pick, dispatches) {
      const extra = { cycsts: null, jtgj: '', ypjjry: '' }
      const match = (dispatches || []).find(d => {
        const sj = this.dispatchSj(d)
        return String(sj.routine_pick_id || '') === String(pick.id || '')
      })
      if (match) {
        const s = this.dispatchSj(match)
        if (s.cycsts !== undefined && s.cycsts !== null && s.cycsts !== '') extra.cycsts = s.cycsts
        if (s.jtgj) extra.jtgj = s.jtgj
        if (s.ypjjry) extra.ypjjry = s.ypjjry
      }
      if (!extra.ypjjry && pick.sampler_xm) extra.ypjjry = pick.sampler_xm
      return extra
    },
    async loadUsers() {
      try {
        const res = await api.get('yn_yh_list')
        this.users = (res.data || []).filter(u => u.zt == 1)
      } catch (e) { this.users = [] }
    },
    async load() {
      this.loading = true
      try {
        const res = await api.get('yn_routine_plan_list', { xmbh: this.xmbh })
        if (res.data && res.data.error) {
          this.$message.error(res.data.error)
          return
        }
        this.project = res.data.project
        this.canEdit = res.data.can_edit !== undefined
          ? !!res.data.can_edit
          : canEditRoutinePlan(res.data.project)
        this.canEditDispatch = res.data.can_edit_dispatch !== undefined
          ? !!res.data.can_edit_dispatch
          : this.canEdit
        this.samplers = res.data.samplers || []
        if (!this.samplers.length) {
          this.samplers = this.users.filter(u => u.zt == 1)
        }
        this.items = res.data.items || []
        this.dispatches = res.data.dispatches || []
        this.picks = (res.data.picks || []).map(p => {
          const pick = {
            ...p,
            sampler_id: p.sampler_id ? parseInt(p.sampler_id, 10) : null,
            sampler_locked: !!(p.sampler_locked === 1 || p.sampler_locked === true)
          }
          pick.dispatch_extra = this.seedDispatchExtraForPick(pick, this.dispatches)
          return pick
        })
      } finally { this.loading = false }
    },
    buildDispatchExtrasPayload() {
      const map = {}
      this.picks.forEach(p => {
        if (!p.id) return
        const d = p.dispatch_extra || {}
        const extra = {}
        if (d.cycsts !== undefined && d.cycsts !== null && d.cycsts !== '') {
          extra.cycsts = d.cycsts
        } else {
          extra.cycsts = 1
        }
        if (d.jtgj) extra.jtgj = d.jtgj
        const yp = d.ypjjry || p.sampler_xm
        if (yp) extra.ypjjry = yp
        map[p.id] = extra
      })
      return map
    },
    async savePlan() {
      if (!this.planEditable) return
      const res = await api.postJson('yn_routine_plan_save', {
        xmbh: this.xmbh,
        items: this.items,
        picks: this.picks
      })
      if (res.data && res.data.error) {
        this.$message.error(res.data.error)
        return
      }
      this.$message.success('已保存周期安排')
      if (res.data && res.data.xmzt) {
        this.project = { ...this.project, xmzt: res.data.xmzt }
      }
    },
    async confirmPlan() {
      if (!this.planEditable) return
      const missingPicks = this.picks.filter(p => !p.sampler_id)
      if (missingPicks.length) {
        this.$message.warning('请为每条月度安排指定采样人员')
        return
      }
      await this.savePlan()
      this.confirming = true
      try {
        const res = await api.postJson('yn_routine_plan_confirm', {
          xmbh: this.xmbh,
          items: this.items,
          picks: this.picks,
          dispatch_extras: this.buildDispatchExtrasPayload()
        })
        if (res.data && res.data.error) {
          this.$message.error(res.data.error)
          return
        }
        const n = res.data.dispatch_count || 1
        this.dispatches = res.data.dispatches || []
        this.$message.success(`已生成 ${n} 张派遣单`)
        if (this.project && res.data.xmzt) {
          this.project = { ...this.project, xmzt: res.data.xmzt }
        }
        if (!this.dispatches.length) await this.load()
      } finally { this.confirming = false }
    },
    async updateDispatchDefaults() {
      if (!this.canEditDispatch) return
      this.updatingDispatch = true
      try {
        const res = await api.postJson('yn_routine_update_dispatch_defaults', {
          xmbh: this.xmbh,
          picks: this.picks,
          dispatch_extras: this.buildDispatchExtrasPayload()
        })
        if (res.data && res.data.error) {
          this.$message.error(res.data.error)
          return
        }
        this.$message.success(`已更新 ${res.data.updated || 0} 张派遣单`)
        this.dispatches = res.data.dispatches || []
        await this.load()
      } finally { this.updatingDispatch = false }
    },
    pickForDispatch(dispatchRow) {
      const sj = this.dispatchSj(dispatchRow)
      const pickId = sj.routine_pick_id
      if (pickId) {
        return this.picks.find(p => String(p.id) === String(pickId)) || null
      }
      const month = sj.plan_month
      const period = sj.plan_period
      if (month != null && period != null) {
        return this.picks.find(p =>
          String(p.plan_month) === String(month) && String(p.plan_period) === String(period)
        ) || null
      }
      return null
    },
    focusPickForDispatch(dispatchRow) {
      const pick = this.pickForDispatch(dispatchRow)
      if (!pick) {
        this.$message.info('未找到对应期次')
        return
      }
      this.activeDispatchId = dispatchRow.id
      const table = this.$refs.pickTable
      if (!table) return
      table.toggleRowExpansion(pick, true)
      this.$nextTick(() => {
        const rows = table.$el.querySelectorAll('.el-table__body-wrapper tbody tr')
        for (let i = 0; i < rows.length; i++) {
          const rowEl = rows[i]
          if (rowEl.classList.contains('el-table__row') && rowEl.textContent.includes(`${pick.plan_month}月`)) {
            rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            break
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.routine-plan-page {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 4px 24px;
}

.plan-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  padding: 20px 24px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #f0f7ff 0%, #fafbfc 100%);
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.hero-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.hero-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-bottom: 10px;
}

.meta-item label {
  color: #909399;
  margin-right: 6px;
  font-size: 12px;
}

.meta-item {
  font-size: 13px;
  color: #606266;
}

.hero-items {
  margin-top: 8px;
}

.hero-item-chip {
  font-size: 12px;
  color: #606266;
  line-height: 1.6;
  padding: 6px 10px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  margin-bottom: 4px;
}

.chip-place { font-weight: 500; color: #303133; }
.chip-sep { margin: 0 4px; color: #c0c4cc; }

.hero-actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.stat-pills {
  display: flex;
  gap: 10px;
}

.stat-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 56px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.stat-num {
  font-size: 20px;
  font-weight: 600;
  color: #409eff;
  line-height: 1.2;
}

.stat-label {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.action-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.bulk-select { width: 140px; }

.action-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  max-width: 260px;
  text-align: right;
}

.sampler-locked-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.lock-tag { margin-left: 0; }

.plan-alert { margin-bottom: 16px; }

.plan-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  gap: 16px;
  align-items: start;
}

.plan-body-full {
  grid-template-columns: 1fr;
}

@media (max-width: 960px) {
  .plan-body { grid-template-columns: 1fr; }
  .plan-hero { flex-direction: column; }
  .hero-actions { align-items: stretch; width: 100%; }
}

.plan-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.plan-card-main {
  min-width: 0;
  grid-column: 1;
}

.pick-extra-panel {
  padding: 8px 16px 12px 48px;
  background: #fff;
}

.pick-extra-card {
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.pick-extra-label {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 10px;
}

.pick-extra-form >>> .el-form-item {
  margin-bottom: 0;
}

.card-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.card-head-title {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.card-head-hint {
  font-size: 12px;
  color: #909399;
}

.period-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 12px;
  background: #ecf5ff;
  color: #409eff;
  border-radius: 4px;
  white-space: nowrap;
}

.cell-text { font-size: 13px; }

.plan-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dispatch-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
}

.dispatch-item {
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.dispatch-item:hover {
  border-color: #c6e2ff;
  background: #f5f9ff;
}

.dispatch-item.is-active {
  border-color: #409eff;
  background: #ecf5ff;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.15);
}

.dispatch-item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.dispatch-rwbh {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.dispatch-item-meta {
  font-size: 12px;
  color: #606266;
}

.meta-dot { margin: 0 4px; color: #c0c4cc; }

.dispatch-item-place {
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-card-empty {
  min-height: 120px;
}
</style>
